/**
 * Property Form Page
 * Formulario de cadastro/edicao de imovel
 *
 * Sections (cards com FormSection/FormField shared):
 * - Fotos do Imovel (media.photos)
 * - Informacoes Basicas (title, description, reference, propertyType, itemStatus)
 * - Endereco (street, number, complement, neighborhood, city, state, zipCode, showAddress)
 * - Caracteristicas (bedrooms, bathrooms, suites, parkingSpaces, areas, floors)
 * - Construcao (year, condition) — colapsavel
 * - Oportunidades de Negocio (BusinessOpportunity[]) — sub-itens editados via botao -> modal (DS 1.9)
 * - Comodidades (amenities) — colapsavel
 * - Tags — colapsavel
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useDatabase } from '../hooks/useDatabases';
import { useCreateDatabaseDocument, useDatabaseDocument, useNextRegistrationCode, useUpdateDatabaseDocument } from '../hooks/useDatabaseDocuments';
import { PhotoUploader } from '../components/PhotoUploader';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Textarea } from '@/shared/components/ui/Textarea';
import { MoneyInput } from '@/shared/components/ui/MoneyInput';
import { FormSection } from '@/shared/components/ui/FormSection';
import { FormField } from '@/shared/components/ui/FormField';
import { Checkbox } from '@/shared/components/ui/Checkbox';
import { Breadcrumb } from '@/shared/components/ui/Breadcrumb';
import { Button } from '@/shared/components/ui/Button';
import { extractApiFieldErrors } from '@/shared/utils/apiFieldErrors';
import type {
  DatabasePropertyData,
  PropertyType,
  PropertyItemStatus,
  BusinessOpportunity,
  BusinessOpportunityType,
  OpportunityStatus,
  DatabaseType,
} from '@troia-v3/types';
import { Icon } from '@/shared/icons';

interface PropertyFormPageProps {
  documentId?: string;
}

// ── Option lists ──

const propertyTypeOptions = [
  { value: 'house', label: 'Casa' },
  { value: 'apartment', label: 'Apartamento' },
  { value: 'commercial', label: 'Comercial' },
  { value: 'land', label: 'Terreno' },
  { value: 'farm', label: 'Fazenda' },
  { value: 'warehouse', label: 'Galpao' },
  { value: 'office', label: 'Escritorio' },
  { value: 'studio', label: 'Studio' },
  { value: 'penthouse', label: 'Cobertura' },
  { value: 'townhouse', label: 'Sobrado' },
];

const statusOptions = [
  { value: 'available', label: 'Disponivel' },
  { value: 'unavailable', label: 'Indisponivel' },
  { value: 'under_construction', label: 'Em Construcao' },
  { value: 'launch', label: 'Lancamento' },
  { value: 'reserved', label: 'Reservado' },
  { value: 'archived', label: 'Arquivado' },
];

const opportunityTypeOptions = [
  { value: 'sale', label: 'Venda' },
  { value: 'rent', label: 'Aluguel' },
  { value: 'seasonal_rent', label: 'Temporada' },
  { value: 'daily_rent', label: 'Diaria' },
  { value: 'lease', label: 'Arrendamento' },
  { value: 'exchange', label: 'Permuta' },
  { value: 'donation', label: 'Doacao' },
  { value: 'auction', label: 'Leilao' },
];

const opportunityStatusOptions = [
  { value: 'active', label: 'Ativo' },
  { value: 'inactive', label: 'Inativo' },
  { value: 'reserved', label: 'Reservado' },
  { value: 'in_negotiation', label: 'Em Negociacao' },
];

const conditionOptions = [
  { value: 'new', label: 'Novo' },
  { value: 'excellent', label: 'Excelente' },
  { value: 'good', label: 'Bom' },
  { value: 'needs_renovation', label: 'Precisa Reforma' },
  { value: 'under_renovation', label: 'Em Reforma' },
];

const periodOptions = [
  { value: 'day', label: 'Dia' },
  { value: 'month', label: 'Mes' },
  { value: 'year', label: 'Ano' },
];

// Modais SEMPRE tema claro fixo — override das CSS variables no container (CLAUDE.md front)
const MODAL_LIGHT_VARS = {
  '--theme-text': '#1e293b',
  '--theme-muted-text': '#6b7280',
  '--theme-input-background': '#ffffff',
  '--theme-input-text': '#1e293b',
  '--theme-input-border': 'rgba(0,0,0,0.10)',
  '--theme-input-focus': '#8b5cf6',
  '--theme-error-color': '#ef4444',
  '--theme-card-background': '#ffffff',
  '--theme-card-text': '#334155',
  '--theme-card-border': 'rgba(0,0,0,0.06)',
  '--theme-card-muted': '#94a3b8',
  '--theme-primary': '#8b5cf6',
  '--theme-primary-background': '#f3e8ff',
  '--theme-button-primary': '#8b5cf6',
  '--theme-button-primary-text': '#ffffff',
  '--theme-button-primary-hover': '#7c3aed',
  '--theme-border': 'rgba(0,0,0,0.06)',
  '--ring-color': 'rgba(139,92,246,0.15)',
} as React.CSSProperties;

// ── Form types ──

interface OpportunityFormData {
  id: string;
  type: string;
  status: string;
  amount: number | undefined;
  period: string;
  negotiable: boolean;
  acceptsExchange: boolean;
}

interface PropertyFormState {
  title: string;
  description: string;
  internalDescription: string;
  reference: string;
  propertyType: string;
  itemStatus: string;
  // Address
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
  zipCode: string;
  showAddress: boolean;
  // Features
  bedrooms: string;
  bathrooms: string;
  suites: string;
  parkingSpaces: string;
  totalArea: string;
  builtArea: string;
  privateArea: string;
  floors: string;
  // Construction
  constructionYear: string;
  constructionCondition: string;
  // Media
  photos: string[];
  /** Liga quando a PESSOA reordena as fotos — faz a ordem sobreviver à
   *  sincronização com o sistema externo (ver media.photosManualOrder). */
  photosManualOrder: boolean;
  // Opportunities
  opportunities: OpportunityFormData[];
  // Amenities & Tags
  amenities: string[];
  tags: string[];
}

const defaultForm: PropertyFormState = {
  title: '',
  description: '',
  internalDescription: '',
  reference: '',
  propertyType: 'house',
  itemStatus: 'available',
  street: '',
  number: '',
  complement: '',
  neighborhood: '',
  city: '',
  state: '',
  zipCode: '',
  showAddress: false,
  bedrooms: '',
  bathrooms: '',
  suites: '',
  parkingSpaces: '',
  totalArea: '',
  builtArea: '',
  privateArea: '',
  floors: '',
  constructionYear: '',
  constructionCondition: '',
  photos: [],
  photosManualOrder: false,
  opportunities: [],
  amenities: [],
  tags: [],
};

// ── Padrão de erros de validação (spec 2026-08-05-friendly-validation-errors) ──
// path do backend → campo do form (label pt-BR + testid pra scroll + formKey pra limpar ao editar)
interface FieldMeta { path: string; formKey: keyof PropertyFormState | null; label: string; testId: string }

const FIELD_META: FieldMeta[] = [
  { path: 'title', formKey: 'title', label: 'Título do imóvel', testId: 'property-field-title' },
  { path: 'propertyType', formKey: 'propertyType', label: 'Tipo de imóvel', testId: 'property-field-type' },
  { path: 'itemStatus', formKey: 'itemStatus', label: 'Status', testId: 'property-field-status' },
  { path: 'reference', formKey: 'reference', label: 'Referência', testId: 'property-field-reference' },
  { path: 'description', formKey: 'description', label: 'Descrição', testId: 'property-field-description' },
  { path: 'internalDescription', formKey: 'internalDescription', label: 'Descrição interna', testId: 'property-field-internal-description' },
  { path: 'address.street', formKey: 'street', label: 'Rua', testId: 'property-field-street' },
  { path: 'address.number', formKey: 'number', label: 'Número', testId: 'property-field-number' },
  { path: 'address.complement', formKey: 'complement', label: 'Complemento', testId: 'property-field-complement' },
  { path: 'address.neighborhood', formKey: 'neighborhood', label: 'Bairro', testId: 'property-field-neighborhood' },
  { path: 'address.city', formKey: 'city', label: 'Cidade', testId: 'property-field-city' },
  { path: 'address.state', formKey: 'state', label: 'Estado', testId: 'property-field-state' },
  { path: 'address.zipCode', formKey: 'zipCode', label: 'CEP', testId: 'property-field-zipcode' },
  { path: 'features.bedrooms', formKey: 'bedrooms', label: 'Dormitórios', testId: 'property-field-bedrooms' },
  { path: 'features.bathrooms', formKey: 'bathrooms', label: 'Banheiros', testId: 'property-field-bathrooms' },
  { path: 'features.suites', formKey: 'suites', label: 'Suítes', testId: 'property-field-suites' },
  { path: 'features.parkingSpaces', formKey: 'parkingSpaces', label: 'Vagas', testId: 'property-field-parking' },
  { path: 'features.totalArea', formKey: 'totalArea', label: 'Área total', testId: 'property-field-totalarea' },
  { path: 'features.builtArea', formKey: 'builtArea', label: 'Área construída', testId: 'property-field-builtarea' },
  { path: 'features.privateArea', formKey: 'privateArea', label: 'Área privativa', testId: 'property-field-privatearea' },
  { path: 'features.floors', formKey: 'floors', label: 'Andares', testId: 'property-field-floors' },
  { path: 'construction.year', formKey: 'constructionYear', label: 'Ano de construção', testId: 'property-field-constructionyear' },
  { path: 'construction.condition', formKey: 'constructionCondition', label: 'Condição', testId: 'property-field-condition' },
];

// Sub-labels amigáveis pra paths de oportunidade (UI é modal/lista, não input direto)
const OPPORTUNITY_SUBFIELD_LABELS: Record<string, string> = {
  'id': 'Identificador',
  'type': 'Tipo',
  'status': 'Status',
  'pricing.amount': 'Preço',
  'pricing.period': 'Período',
};

// ── Helpers ──

const isRentType = (type: string) =>
  ['rent', 'seasonal_rent', 'daily_rent', 'lease'].includes(type);

const typeLabel = (t: string) => opportunityTypeOptions.find(o => o.value === t)?.label ?? t;
const statusLabel = (s: string) => opportunityStatusOptions.find(o => o.value === s)?.label ?? s;
const periodLabel = (p: string) => (periodOptions.find(o => o.value === p)?.label ?? p).toLowerCase();

/** Preço da oportunidade como o VISITANTE vai ver: sem valor (vazio ou zero)
 *  o site publica "Consulte", e o painel mostra a mesma palavra para não
 *  prometer uma coisa aqui e outra lá. */
const formatBRL = (v?: number) =>
  !v
    ? 'Consulte'
    : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

// Chip usado nas linhas de leitura das oportunidades
const OppChip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span
    className="inline-flex items-center h-[22px] px-2 rounded-full text-xs"
    style={{
      backgroundColor: 'var(--theme-page-background)',
      border: '1px solid var(--theme-card-border)',
      color: 'var(--theme-muted-text)',
    }}
  >
    {children}
  </span>
);

// ── Main Component ──

export const PropertyFormPage: React.FC<PropertyFormPageProps> = ({ documentId }) => {
  const { id: databaseId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!documentId;

  const { data: database } = useDatabase(databaseId);
  const { data: existingDocument, isLoading: isLoadingDocument } = useDatabaseDocument<DatabasePropertyData>(
    documentId || '',
    isEditing
  );
  const createMutation = useCreateDatabaseDocument();
  const updateMutation = useUpdateDatabaseDocument<DatabasePropertyData>();

  const [form, setForm] = useState<PropertyFormState>(defaultForm);
  const [formLoaded, setFormLoaded] = useState(false);
  const [hasChanges, setHasChanges] = useState(!isEditing);
  // Enquanto a pessoa não mexer na Referência, a sugestão pode se atualizar
  // sozinha (ex.: trocou o tipo do imóvel de casa para apartamento). Depois que
  // ela digita, o campo é dela e nada mais o sobrescreve.
  const [referenciaTocada, setReferenciaTocada] = useState(false);
  const [amenityInput, setAmenityInput] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Sugestão do próximo código — só em cadastro NOVO. Em base sincronizada a
  // rota devolve vazio de propósito (registro criado à mão ali seria arquivado
  // na rodada seguinte da sincronização).
  const { data: proximoCodigo, refetch: recarregarCodigo } = useNextRegistrationCode(databaseId, {
    propertyType: form.propertyType,
    enabled: !isEditing,
  });

  useEffect(() => {
    if (isEditing || referenciaTocada) return;
    const sugestao = proximoCodigo?.sugestao;
    if (!sugestao || sugestao === form.reference) return;
    setForm((prev) => ({ ...prev, reference: sugestao }));
  }, [isEditing, referenciaTocada, proximoCodigo?.sugestao, form.reference]);

  // Opportunity modal state (DS 1.9 — sub-itens editados via botao -> modal)
  const [oppModal, setOppModal] = useState<{ index: number | null; draft: OpportunityFormData } | null>(null);
  const [oppError, setOppError] = useState<string | null>(null);
  const [deleteOppIndex, setDeleteOppIndex] = useState<number | null>(null);

  // Populate form with existing data when editing
  useEffect(() => {
    if (isEditing && existingDocument && !formLoaded) {
      const property = existingDocument.data;
      setForm({
        title: property.title || '',
        description: property.description || '',
        internalDescription: property.internalDescription || '',
        reference: property.reference || '',
        propertyType: property.propertyType || 'house',
        itemStatus: property.itemStatus || 'available',
        street: property.address?.street || '',
        number: property.address?.number || '',
        complement: property.address?.complement || '',
        neighborhood: property.address?.neighborhood || '',
        city: property.address?.city || '',
        state: property.address?.state || '',
        zipCode: property.address?.zipCode || '',
        showAddress: property.showAddress ?? false,
        bedrooms: property.features?.bedrooms?.toString() || '',
        bathrooms: property.features?.bathrooms?.toString() || '',
        suites: property.features?.suites?.toString() || '',
        parkingSpaces: property.features?.parkingSpaces?.toString() || '',
        totalArea: property.features?.totalArea?.toString() || '',
        builtArea: property.features?.builtArea?.toString() || '',
        privateArea: property.features?.privateArea?.toString() || '',
        floors: property.features?.floors?.toString() || '',
        constructionYear: property.construction?.year?.toString() || '',
        constructionCondition: property.construction?.condition || '',
        photos: property.media?.photos || [],
        photosManualOrder: property.media?.photosManualOrder === true,
        opportunities: property.opportunities?.map(o => ({
          id: o.id,
          type: o.type,
          status: o.status,
          amount: o.pricing?.amount,
          period: o.pricing?.period || 'month',
          negotiable: o.conditions?.negotiable || false,
          acceptsExchange: o.conditions?.acceptsExchange || false,
        })) || [],
        amenities: property.amenities || [],
        tags: property.tags || [],
      });
      setFormLoaded(true);
      setHasChanges(false);
    }
  }, [isEditing, existingDocument, formLoaded]);

  const handleBack = () => {
    if (isEditing && documentId) {
      navigate(`/databases/${databaseId}/documents/${documentId}`);
    } else {
      navigate(`/databases/${databaseId}`);
    }
  };

  if (isEditing && isLoadingDocument) {
    return (
      <div className="min-h-full flex items-center justify-center" style={{ backgroundColor: 'var(--theme-page-background)' }}>
        <div className="flex flex-col items-center gap-4">
          <Icon name="loader" className="w-8 h-8 animate-spin" style={{ color: 'var(--theme-primary)' }} />
          <span style={{ color: 'var(--theme-muted-text)' }}>Carregando imóvel...</span>
        </div>
      </div>
    );
  }

  const updateField = <K extends keyof PropertyFormState>(field: K, value: PropertyFormState[K]) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
    setFieldErrors((prev) => {
      const meta = FIELD_META.find((f) => f.formKey === field);
      if (!meta || !prev[meta.path]) return prev;
      const next = { ...prev };
      delete next[meta.path];
      return next;
    });
  };

  // ── Opportunities (modal-based, DS 1.9) ──
  const openNewOpp = () => {
    setOppError(null);
    setOppModal({
      index: null,
      draft: {
        id: crypto.randomUUID(),
        type: 'sale',
        status: 'active',
        amount: undefined,
        period: 'month',
        negotiable: false,
        acceptsExchange: false,
      },
    });
  };

  const openEditOpp = (index: number) => {
    const opp = form.opportunities[index];
    if (!opp) return;
    setOppError(null);
    setOppModal({ index, draft: { ...opp } });
  };

  const updateDraft = (updates: Partial<OpportunityFormData>) => {
    setOppModal(prev => (prev ? { ...prev, draft: { ...prev.draft, ...updates } } : prev));
  };

  const commitOpp = () => {
    if (!oppModal) return;
    const draft = oppModal.draft;
    // Preço em branco ou zero é permitido: significa "sob consulta", e o site
    // exibe "Consulte" no lugar do valor. Em produção já existem imóveis assim.
    const list = [...form.opportunities];
    if (oppModal.index === null) {
      list.push(draft);
    } else {
      list[oppModal.index] = draft;
    }
    updateField('opportunities', list);
    setOppModal(null);
  };

  const confirmDeleteOpp = () => {
    if (deleteOppIndex !== null) {
      updateField('opportunities', form.opportunities.filter((_, i) => i !== deleteOppIndex));
    }
    setDeleteOppIndex(null);
  };

  // ── Amenities ──
  const addAmenity = () => {
    if (amenityInput.trim() && !form.amenities.includes(amenityInput.trim())) {
      updateField('amenities', [...form.amenities, amenityInput.trim()]);
      setAmenityInput('');
    }
  };

  const removeAmenity = (amenity: string) => {
    updateField('amenities', form.amenities.filter(a => a !== amenity));
  };

  // ── Tags ──
  const addTag = () => {
    if (tagInput.trim() && !form.tags.includes(tagInput.trim())) {
      updateField('tags', [...form.tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const removeTag = (tag: string) => {
    updateField('tags', form.tags.filter(t => t !== tag));
  };

  // ── Save ──
  const handleSave = async () => {
    setError(null);
    setFieldErrors({});

    if (!form.title.trim()) {
      setError('Titulo e obrigatorio');
      return;
    }
    if (form.opportunities.length === 0) {
      setError('Adicione pelo menos uma oportunidade de negocio');
      return;
    }

    setSaving(true);

    try {
      const now = new Date().toISOString();

      const opportunities: BusinessOpportunity[] = form.opportunities.map(o => ({
        id: o.id,
        type: o.type as BusinessOpportunityType,
        status: o.status as OpportunityStatus,
        pricing: {
          amount: o.amount ?? 0,
          currency: 'BRL',
          period: isRentType(o.type) ? o.period as 'day' | 'month' | 'year' : undefined,
        },
        conditions: (o.negotiable || o.acceptsExchange) ? {
          negotiable: o.negotiable || undefined,
          acceptsExchange: o.acceptsExchange || undefined,
        } : undefined,
        createdAt: now,
        updatedAt: now,
      }));

      const toNum = (v: string) => v ? Number(v) || undefined : undefined;

      const propertyData: DatabasePropertyData = {
        title: form.title.trim(),
        description: form.description.trim(),
        internalDescription: form.internalDescription.trim() || undefined,
        reference: form.reference.trim() || undefined,
        propertyType: form.propertyType as PropertyType,
        address: {
          street: form.street.trim(),
          number: form.number.trim() || undefined,
          complement: form.complement.trim() || undefined,
          neighborhood: form.neighborhood.trim(),
          city: form.city.trim(),
          state: form.state.trim(),
          zipCode: form.zipCode.trim(),
          country: 'BR',
        },
        features: {
          bedrooms: toNum(form.bedrooms),
          bathrooms: toNum(form.bathrooms),
          suites: toNum(form.suites),
          parkingSpaces: toNum(form.parkingSpaces),
          totalArea: toNum(form.totalArea),
          builtArea: toNum(form.builtArea),
          privateArea: toNum(form.privateArea),
          floors: toNum(form.floors),
        },
        construction: (form.constructionYear || form.constructionCondition) ? {
          year: toNum(form.constructionYear),
          condition: form.constructionCondition as DatabasePropertyData['construction'] extends { condition?: infer C } ? C : never || undefined,
        } : undefined,
        opportunities,
        itemStatus: form.itemStatus as PropertyItemStatus,
        media: form.photos.length > 0
          ? { photos: form.photos, ...(form.photosManualOrder ? { photosManualOrder: true } : {}) }
          : undefined,
        amenities: form.amenities.length > 0 ? form.amenities : undefined,
        tags: form.tags.length > 0 ? form.tags : undefined,
        showAddress: form.showAddress,
      };

      if (isEditing && documentId) {
        await updateMutation.mutateAsync({
          documentId,
          data: propertyData,
        });
        navigate(`/databases/${databaseId}/documents/${documentId}`);
      } else {
        await createMutation.mutateAsync({
          databaseId: databaseId!,
          type: 'properties' as DatabaseType,
          data: propertyData,
          metadata: { source: 'manual' },
        });
        navigate(`/databases/${databaseId}`);
      }
    } catch (err: unknown) {
      const parsed = extractApiFieldErrors(err);
      if (parsed.hasFieldErrors) {
        setFieldErrors(parsed.fieldErrors);
        // Referência recusada = alguém usou o código enquanto esta tela estava
        // aberta. Recarrega a sugestão, senão os atalhos de prefixo continuariam
        // oferecendo o código que acabou de ser tomado.
        if (parsed.fieldErrors['reference']) void recarregarCodigo();
        // Residual pro banner: paths sem campo direto no form (ex:
        // opportunities.0.pricing.amount) OU com campo mapeado mas SEM
        // elemento no DOM (seção FormSection defaultCollapsed desmonta
        // children — ex: construction.year/condition) — sem feedback
        // inline visível, então precisam aparecer no banner.
        const residuals = Object.entries(parsed.fieldErrors)
          .filter(([path]) => {
            const meta = FIELD_META.find((f) => f.path === path);
            if (!meta) return true; // path sem campo no form
            return !window.document.querySelector(`[data-testid="${meta.testId}"]`); // seção colapsada
          })
          .map(([path, msg]) => {
            if (path === '_root') return msg;
            const oppMatch = path.match(/^opportunities\.(\d+)(?:\.(.+))?$/);
            if (oppMatch) {
              const idx = Number(oppMatch[1]) + 1;
              const sub = oppMatch[2] ? OPPORTUNITY_SUBFIELD_LABELS[oppMatch[2]] || oppMatch[2] : '';
              return `Oportunidade ${idx}${sub ? ` — ${sub}` : ''}: ${msg}`;
            }
            const meta = FIELD_META.find((f) => f.path === path);
            return meta ? `${meta.label}: ${msg}` : `${path}: ${msg}`;
          });
        setError(residuals.length > 0 ? residuals.join('\n') : null);
        toast.error('Corrija os campos destacados');
        // Scroll até o primeiro campo COM elemento no DOM, na ordem do form
        // (campos em seções colapsadas não têm elemento pra scrollar até)
        const first = FIELD_META.find((f) => parsed.fieldErrors[f.path]
          && window.document.querySelector(`[data-testid="${f.testId}"]`));
        if (first) {
          const el = window.document.querySelector(`[data-testid="${first.testId}"]`);
          el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (el && typeof (el as HTMLElement).focus === 'function') {
            (el as HTMLElement).focus({ preventScroll: true });
          }
        }
      } else {
        setError(parsed.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const currentLabel = isEditing
    ? (form.reference.trim() || form.title.trim() || 'Editar imóvel')
    : 'Novo imóvel';

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* Sticky Header — breadcrumb + acoes */}
      <div
        className="sticky top-0 z-20"
        style={{
          backgroundColor: 'var(--theme-card-background)',
          borderBottom: '1px solid var(--theme-card-border)',
        }}
      >
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-4">
          <div className="flex items-center justify-between gap-4">
            {/* Breadcrumb shared (F25-31; nunca seta-voltar) */}
            <Breadcrumb
              items={[
                { label: 'Bases de dados', onNavigate: () => navigate('/databases'), testid: 'property-form-bc-databases' },
                { label: database?.name || 'Base', onNavigate: () => navigate(`/databases/${databaseId}`), testid: 'property-form-bc-database' },
                { label: currentLabel, testid: 'property-form-title' },
              ]}
              mobileLegend={`Bases de dados · ${database?.name || 'Base'}`}
              data-testid="property-form-breadcrumb"
            />

            {/* Acoes (Button shared, F01-03) */}
            <div className="flex items-center gap-3 flex-shrink-0">
              {hasChanges && (
                <span className="hidden md:inline text-sm" style={{ color: 'var(--theme-warning-color)' }}>
                  {isEditing ? 'Alterações não salvas' : 'Preencha os campos obrigatórios'}
                </span>
              )}
              <Button
                variant="secondary"
                onClick={handleBack}
                disabled={saving}
                className="hidden sm:inline-flex"
                data-testid="property-form-cancel"
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                icon="check"
                loading={saving}
                onClick={handleSave}
                data-testid="property-form-save"
              >
                {isEditing ? 'Salvar' : 'Criar imóvel'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-8 space-y-6">
        {error && (
          <div
            className="p-4 rounded-lg text-sm whitespace-pre-line"
            style={{
              backgroundColor: 'rgba(239,68,68,0.1)',
              color: 'var(--theme-error-color)',
              border: '1px solid var(--theme-error-color)',
            }}
            data-testid="property-form-error"
          >
            {error}
          </div>
        )}

        {/* Fotos */}
        <FormSection title="Fotos do Imóvel" icon="image" description="A primeira foto é a principal">
          <PhotoUploader
            photos={form.photos}
            folder="properties"
            idPrefix="property"
            onChange={(photos, opcoes) => {
              setForm((prev) => ({
                ...prev,
                photos,
                // A marca nunca é desligada aqui: quem já assumiu a ordem segue
                // no comando, mesmo depois de subir ou remover uma foto.
                photosManualOrder: prev.photosManualOrder || opcoes?.reordenadoAMao === true,
              }));
            }}
          />
        </FormSection>

        {/* Informacoes Basicas */}
        <FormSection title="Informações Básicas" icon="home" description="Identificação do imóvel">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <FormField label="Título do imóvel" required error={fieldErrors['title']}>
                <Input
                  value={form.title}
                  onChange={(e) => updateField('title', e.target.value)}
                  placeholder="Ex: Apartamento 3 quartos"
                  error={fieldErrors['title']}
                  data-testid="property-field-title"
                />
              </FormField>
            </div>

            <FormField label="Tipo de imóvel" required>
              <Select
                value={form.propertyType}
                onChange={(value) => updateField('propertyType', value)}
                options={propertyTypeOptions}
                error={fieldErrors['propertyType']}
                data-testid="property-field-type"
              />
            </FormField>

            <FormField label="Status">
              <Select
                value={form.itemStatus}
                onChange={(value) => updateField('itemStatus', value)}
                options={statusOptions}
                error={fieldErrors['itemStatus']}
                data-testid="property-field-status"
              />
            </FormField>

            <FormField
              label="Referência"
              error={fieldErrors['reference']}
              description={!isEditing && proximoCodigo?.sugestao ? 'Sugerido pelo sistema — pode alterar' : undefined}
            >
              <Input
                value={form.reference}
                onChange={(e) => {
                  setReferenciaTocada(true);
                  updateField('reference', e.target.value);
                }}
                placeholder="Codigo interno"
                error={fieldErrors['reference']}
                data-testid="property-field-reference"
              />
              {/* Prefixos que a base já usa, com o próximo livre de cada — trocar
                  de sequência (casa → terreno) em um clique, sem digitar. */}
              {!isEditing && (proximoCodigo?.prefixos.length ?? 0) > 1 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {proximoCodigo?.prefixos.map((p) => (
                    <button
                      key={p.prefixo}
                      type="button"
                      onClick={() => {
                        setReferenciaTocada(true);
                        updateField('reference', p.proximo);
                      }}
                      className="px-2 py-1 rounded-md text-xs font-medium border transition-colors"
                      style={{
                        borderColor: form.reference === p.proximo ? 'var(--theme-primary)' : 'var(--theme-card-border)',
                        color: form.reference === p.proximo ? 'var(--theme-primary)' : 'var(--theme-muted-text)',
                        backgroundColor: 'transparent',
                      }}
                      data-testid={`property-reference-prefix-${p.prefixo}`}
                    >
                      {p.proximo}
                    </button>
                  ))}
                </div>
              )}
            </FormField>

            <div className="md:col-span-2">
              <FormField label="Descrição">
                <Textarea
                  value={form.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  rows={4}
                  placeholder="Descreva o imovel..."
                  error={fieldErrors['description']}
                  data-testid="property-field-description"
                />
              </FormField>
            </div>
          </div>
        </FormSection>

        {/* Informacoes Internas — nunca vai para o site publico nem para o agente de IA */}
        <FormSection
          key={formLoaded ? 'internal-loaded' : 'internal-initial'}
          title="Informações Internas"
          icon="lock"
          description="Visível só para a equipe — não aparece no site nem para o agente de IA"
          collapsible
          defaultCollapsed={!form.internalDescription}
        >
          <FormField label="Descrição interna">
            <Textarea
              value={form.internalDescription}
              onChange={(e) => updateField('internalDescription', e.target.value)}
              rows={4}
              placeholder="Anotações internas sobre o imóvel..."
              error={fieldErrors['internalDescription']}
              data-testid="property-field-internal-description"
            />
          </FormField>
        </FormSection>

        {/* Endereco */}
        <FormSection title="Endereço" icon="map-pin" description="Localização e exibição pública">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <FormField label="Rua" error={fieldErrors['address.street']}>
                <Input
                  value={form.street}
                  onChange={(e) => updateField('street', e.target.value)}
                  placeholder="Nome da rua"
                  error={fieldErrors['address.street']}
                  data-testid="property-field-street"
                />
              </FormField>
            </div>

            <FormField label="Número" error={fieldErrors['address.number']}>
              <Input
                value={form.number}
                onChange={(e) => updateField('number', e.target.value)}
                placeholder="123"
                error={fieldErrors['address.number']}
                data-testid="property-field-number"
              />
            </FormField>

            <FormField label="Complemento" error={fieldErrors['address.complement']}>
              <Input
                value={form.complement}
                onChange={(e) => updateField('complement', e.target.value)}
                placeholder="Apto 101, Bloco A"
                error={fieldErrors['address.complement']}
                data-testid="property-field-complement"
              />
            </FormField>

            <FormField label="Bairro" error={fieldErrors['address.neighborhood']}>
              <Input
                value={form.neighborhood}
                onChange={(e) => updateField('neighborhood', e.target.value)}
                placeholder="Bairro"
                error={fieldErrors['address.neighborhood']}
                data-testid="property-field-neighborhood"
              />
            </FormField>

            <FormField label="Cidade" error={fieldErrors['address.city']}>
              <Input
                value={form.city}
                onChange={(e) => updateField('city', e.target.value)}
                placeholder="Cidade"
                error={fieldErrors['address.city']}
                data-testid="property-field-city"
              />
            </FormField>

            <FormField label="Estado" error={fieldErrors['address.state']}>
              <Input
                value={form.state}
                onChange={(e) => updateField('state', e.target.value)}
                placeholder="UF"
                maxLength={2}
                style={{ textTransform: 'uppercase' }}
                error={fieldErrors['address.state']}
                data-testid="property-field-state"
              />
            </FormField>

            <FormField label="CEP" error={fieldErrors['address.zipCode']}>
              <Input
                value={form.zipCode}
                onChange={(e) => updateField('zipCode', e.target.value)}
                placeholder="00000-000"
                error={fieldErrors['address.zipCode']}
                data-testid="property-field-zipcode"
              />
            </FormField>

            {/* Exibicao do endereco — toggle segmentado (segue o tema da pagina) */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--theme-text)' }}>
                Exibição do endereço
              </label>
              <div
                className="inline-flex gap-1 p-1 rounded-lg"
                style={{ backgroundColor: 'var(--theme-page-background)', border: '1px solid var(--theme-card-border)' }}
              >
                {[
                  { value: true, label: 'Público' },
                  { value: false, label: 'Privado' },
                ].map((opt) => {
                  const active = form.showAddress === opt.value;
                  return (
                    <button
                      key={String(opt.value)}
                      onClick={() => updateField('showAddress', opt.value)}
                      className="h-8 px-4 rounded-md text-sm font-semibold transition-colors"
                      style={{
                        backgroundColor: active ? 'var(--theme-card-background)' : 'transparent',
                        color: active ? 'var(--theme-primary)' : 'var(--theme-muted-text)',
                        border: active ? '1px solid var(--theme-card-border)' : '1px solid transparent',
                      }}
                      data-testid={`property-field-showaddress-${opt.value ? 'public' : 'private'}`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs mt-2" style={{ color: 'var(--theme-muted-text)' }}>
                Privado: o endereço completo não aparece no site nem nas respostas do agente
              </p>
            </div>
          </div>
        </FormSection>

        {/* Caracteristicas */}
        <FormSection title="Características" icon="grid" description="Números do imóvel">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <FormField label="Quartos" error={fieldErrors['features.bedrooms']}>
              <Input type="number" value={form.bedrooms} onChange={(e) => updateField('bedrooms', e.target.value)} placeholder="0" error={fieldErrors['features.bedrooms']} data-testid="property-field-bedrooms" />
            </FormField>
            <FormField label="Banheiros" error={fieldErrors['features.bathrooms']}>
              <Input type="number" value={form.bathrooms} onChange={(e) => updateField('bathrooms', e.target.value)} placeholder="0" error={fieldErrors['features.bathrooms']} data-testid="property-field-bathrooms" />
            </FormField>
            <FormField label="Suítes" error={fieldErrors['features.suites']}>
              <Input type="number" value={form.suites} onChange={(e) => updateField('suites', e.target.value)} placeholder="0" error={fieldErrors['features.suites']} data-testid="property-field-suites" />
            </FormField>
            <FormField label="Vagas" error={fieldErrors['features.parkingSpaces']}>
              <Input type="number" value={form.parkingSpaces} onChange={(e) => updateField('parkingSpaces', e.target.value)} placeholder="0" error={fieldErrors['features.parkingSpaces']} data-testid="property-field-parking" />
            </FormField>
            <FormField label="Área total (m²)" error={fieldErrors['features.totalArea']}>
              <Input type="number" value={form.totalArea} onChange={(e) => updateField('totalArea', e.target.value)} placeholder="0" error={fieldErrors['features.totalArea']} data-testid="property-field-totalarea" />
            </FormField>
            <FormField label="Área construída (m²)" error={fieldErrors['features.builtArea']}>
              <Input type="number" value={form.builtArea} onChange={(e) => updateField('builtArea', e.target.value)} placeholder="0" error={fieldErrors['features.builtArea']} data-testid="property-field-builtarea" />
            </FormField>
            <FormField label="Área privativa (m²)" error={fieldErrors['features.privateArea']}>
              <Input type="number" value={form.privateArea} onChange={(e) => updateField('privateArea', e.target.value)} placeholder="0" error={fieldErrors['features.privateArea']} data-testid="property-field-privatearea" />
            </FormField>
            <FormField label="Andares" error={fieldErrors['features.floors']}>
              <Input type="number" value={form.floors} onChange={(e) => updateField('floors', e.target.value)} placeholder="0" error={fieldErrors['features.floors']} data-testid="property-field-floors" />
            </FormField>
          </div>
        </FormSection>

        {/* Construcao */}
        <FormSection title="Construção" icon="tool" description="Ano e condição (opcional)" collapsible defaultCollapsed>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Ano de construção" error={fieldErrors['construction.year']}>
              <Input
                type="number"
                value={form.constructionYear}
                onChange={(e) => updateField('constructionYear', e.target.value)}
                placeholder="2024"
                error={fieldErrors['construction.year']}
                data-testid="property-field-constructionyear"
              />
            </FormField>
            <FormField label="Estado de conservação">
              <Select
                value={form.constructionCondition}
                onChange={(value) => updateField('constructionCondition', value)}
                options={conditionOptions}
                placeholder="Selecione..."
                error={fieldErrors['construction.condition']}
                data-testid="property-field-condition"
              />
            </FormField>
          </div>
        </FormSection>

        {/* Oportunidades de Negocio — sub-itens via botao -> modal (DS 1.9) */}
        <FormSection
          title="Oportunidades de Negócio *"
          icon="dollar-sign"
          description={'Pelo menos uma. Sem preço, o site mostra "Consulte"'}
        >
          {form.opportunities.length === 0 ? (
            <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }} data-testid="property-opp-empty">
              Nenhuma oportunidade ainda — adicione pelo menos uma.
            </p>
          ) : (
            <div className="space-y-2">
              {form.opportunities.map((opp, index) => (
                <div
                  key={opp.id}
                  className="flex items-center gap-3 rounded-lg border p-3"
                  style={{ borderColor: 'var(--theme-card-border)' }}
                  data-testid={`property-opp-row-${index}`}
                >
                  <div
                    className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}
                  >
                    <Icon name="tag" className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
                      {typeLabel(opp.type)} · <span className="font-bold">{formatBRL(opp.amount)}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      <OppChip>{statusLabel(opp.status)}</OppChip>
                      {isRentType(opp.type) && opp.period && <OppChip>por {periodLabel(opp.period)}</OppChip>}
                      {opp.negotiable && <OppChip>Negociável</OppChip>}
                      {opp.acceptsExchange && <OppChip>Aceita permuta</OppChip>}
                    </div>
                  </div>
                  <button
                    onClick={() => openEditOpp(index)}
                    className="flex-shrink-0 p-1.5 rounded-lg transition-colors"
                    style={{ color: 'var(--theme-muted-text)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    title="Editar oportunidade"
                    data-testid={`property-opp-edit-${index}`}
                  >
                    <Icon name="edit" className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setDeleteOppIndex(index)}
                    className="flex-shrink-0 p-1.5 rounded-lg transition-colors"
                    style={{ color: 'var(--theme-error-color)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.1)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    title="Remover oportunidade"
                    data-testid={`property-opp-delete-${index}`}
                  >
                    <Icon name="trash" className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <button
            onClick={openNewOpp}
            className="w-full h-10 rounded-lg border border-dashed inline-flex items-center justify-center gap-2 text-sm font-semibold transition-colors"
            style={{ borderColor: 'var(--theme-card-border)', color: 'var(--theme-muted-text)' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            data-testid="property-opp-add"
          >
            <Icon name="plus" className="w-4 h-4" />
            Adicionar oportunidade
          </button>
        </FormSection>

        {/* Comodidades */}
        <FormSection title="Comodidades" icon="grid" description="Diferenciais do imóvel/condomínio" collapsible defaultCollapsed>
          <div className="flex items-center gap-2">
            <Input
              value={amenityInput}
              onChange={(e) => setAmenityInput(e.target.value)}
              placeholder="Nova comodidade (Enter adiciona)"
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); addAmenity(); }
              }}
              data-testid="property-amenity-input"
            />
            <button
              onClick={addAmenity}
              className="px-4 h-[34px] rounded-lg text-sm font-medium transition-colors flex-shrink-0"
              style={{ backgroundColor: 'var(--theme-primary)', color: '#ffffff' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-primary-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-primary)'; }}
              data-testid="property-amenity-add"
            >
              Adicionar
            </button>
          </div>

          {form.amenities.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {form.amenities.map(amenity => (
                <span
                  key={amenity}
                  className="inline-flex items-center gap-1.5 pl-3 pr-2 py-1 rounded-full text-xs font-medium"
                  style={{ backgroundColor: 'var(--theme-page-background)', border: '1px solid var(--theme-card-border)', color: 'var(--theme-card-text)' }}
                  data-testid={`property-amenity-chip-${amenity}`}
                >
                  {amenity}
                  <button onClick={() => removeAmenity(amenity)} className="hover:opacity-70 transition-opacity" aria-label={`Remover ${amenity}`}>
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </FormSection>

        {/* Tags */}
        <FormSection title="Tags" icon="tag" description="Ajudam a busca do agente de IA e os filtros" collapsible defaultCollapsed>
          <div className="flex items-center gap-2">
            <Input
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              placeholder="Nova tag (Enter adiciona)"
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); addTag(); }
              }}
              data-testid="property-tag-input"
            />
            <button
              onClick={addTag}
              className="px-4 h-[34px] rounded-lg text-sm font-medium transition-colors flex-shrink-0"
              style={{ backgroundColor: 'var(--theme-primary)', color: '#ffffff' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-primary-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-primary)'; }}
              data-testid="property-tag-add"
            >
              Adicionar
            </button>
          </div>

          {form.tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {form.tags.map(tag => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1.5 pl-3 pr-2 py-1 rounded-full text-xs font-medium"
                  style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}
                  data-testid={`property-tag-chip-${tag}`}
                >
                  {tag}
                  <button onClick={() => removeTag(tag)} className="hover:opacity-70 transition-opacity" aria-label={`Remover ${tag}`}>
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </FormSection>
      </div>

      {/* Modal: nova/editar oportunidade — tema claro fixo (DS 1.9) */}
      {oppModal && createPortal((
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="property-opp-modal">
          {/* form: NÃO fecha ao clicar fora (DS §Overlays dismissal) */}
          <div
            className="absolute inset-0"
            style={{ backgroundColor: 'rgba(15,23,42,0.34)' }}
          />
          <div
            className="relative w-full max-w-lg rounded-2xl shadow-xl flex flex-col max-h-[88vh]"
            style={{ backgroundColor: '#ffffff', ...MODAL_LIGHT_VARS }}
          >
            <div className="flex items-center gap-3 px-5 py-4 border-b" style={{ borderColor: '#eef1f5' }}>
              <Icon name="dollar-sign" className="w-5 h-5" style={{ color: 'var(--theme-primary)' }} />
              <h3 className="flex-1 text-base font-bold" style={{ color: 'var(--theme-card-text)' }}>
                {oppModal.index === null ? 'Nova oportunidade' : 'Editar oportunidade'}
              </h3>
              <button
                onClick={() => setOppModal(null)}
                className="p-1.5 rounded-lg"
                style={{ color: 'var(--theme-muted-text)' }}
                data-testid="property-opp-modal-close"
              >
                <Icon name="x" className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField label="Tipo">
                  <Select
                    value={oppModal.draft.type}
                    onChange={(v) => updateDraft({ type: v })}
                    options={opportunityTypeOptions}
                    data-testid="property-opp-type"
                  />
                </FormField>
                <MoneyInput
                  label="Preço"
                  value={oppModal.draft.amount}
                  onChange={(v) => { updateDraft({ amount: v }); if (oppError) setOppError(null); }}
                  placeholder={'Deixe vazio para "Consulte"'}
                  hideHelperText
                  error={oppError || undefined}
                  data-testid="property-opp-amount"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField label="Status">
                  <Select
                    value={oppModal.draft.status}
                    onChange={(v) => updateDraft({ status: v })}
                    options={opportunityStatusOptions}
                    data-testid="property-opp-status"
                  />
                </FormField>
                <FormField label="Período (aluguel)">
                  <Select
                    value={oppModal.draft.period}
                    onChange={(v) => updateDraft({ period: v })}
                    options={periodOptions}
                    data-testid="property-opp-period"
                  />
                </FormField>
              </div>
              <div className="flex flex-col gap-3">
                <Checkbox
                  label="Negociável"
                  checked={oppModal.draft.negotiable}
                  onChange={(c) => updateDraft({ negotiable: c })}
                  data-testid="property-opp-negotiable"
                />
                <Checkbox
                  label="Aceita permuta"
                  checked={oppModal.draft.acceptsExchange}
                  onChange={(c) => updateDraft({ acceptsExchange: c })}
                  data-testid="property-opp-exchange"
                />
              </div>
            </div>

            <div className="flex gap-3 px-5 py-4 border-t" style={{ borderColor: '#eef1f5' }}>
              <button
                onClick={() => setOppModal(null)}
                className="flex-1 h-10 rounded-lg border font-semibold text-sm"
                style={{ borderColor: '#d7dde5', color: '#475569' }}
                data-testid="property-opp-modal-cancel"
              >
                Cancelar
              </button>
              <button
                onClick={commitOpp}
                className="flex-1 h-10 rounded-lg font-semibold text-sm inline-flex items-center justify-center gap-2"
                style={{ backgroundColor: 'var(--theme-primary)', color: '#ffffff' }}
                data-testid="property-opp-modal-save"
              >
                <Icon name="check" className="w-4 h-4" />
                {oppModal.index === null ? 'Adicionar oportunidade' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      ), document.body)}

      {/* Modal: remover oportunidade (confirmacao) — tema claro fixo */}
      {deleteOppIndex !== null && createPortal((
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="property-opp-delete-modal">
          {/* confirmação: NÃO fecha ao clicar fora (DS §Overlays dismissal) */}
          <div
            className="absolute inset-0"
            style={{ backgroundColor: 'rgba(15,23,42,0.34)' }}
          />
          <div
            className="relative w-full max-w-sm rounded-2xl shadow-xl flex flex-col"
            style={{ backgroundColor: '#ffffff', ...MODAL_LIGHT_VARS }}
          >
            <div className="flex items-center gap-3 px-5 py-4 border-b" style={{ borderColor: '#eef1f5' }}>
              <Icon name="alert-triangle" className="w-5 h-5" style={{ color: 'var(--theme-error-color)' }} />
              <h3 className="flex-1 text-base font-bold" style={{ color: 'var(--theme-card-text)' }}>
                Remover oportunidade
              </h3>
              <button
                onClick={() => setDeleteOppIndex(null)}
                className="p-1.5 rounded-lg"
                style={{ color: 'var(--theme-muted-text)' }}
                data-testid="property-opp-delete-close"
              >
                <Icon name="x" className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5">
              <p className="text-sm mb-3" style={{ color: 'var(--theme-card-text)' }}>
                Remover esta oportunidade do imóvel?
              </p>
              <div
                className="rounded-lg p-3 text-xs leading-relaxed"
                style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a', color: '#92400e' }}
              >
                O imóvel precisa manter pelo menos uma oportunidade.
              </div>
            </div>
            <div className="flex gap-3 px-5 py-4 border-t" style={{ borderColor: '#eef1f5' }}>
              <button
                onClick={() => setDeleteOppIndex(null)}
                className="flex-1 h-10 rounded-lg border font-semibold text-sm"
                style={{ borderColor: '#d7dde5', color: '#475569' }}
                data-testid="property-opp-delete-cancel"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDeleteOpp}
                className="flex-1 h-10 rounded-lg font-semibold text-sm"
                style={{ backgroundColor: 'var(--theme-error-color)', color: '#ffffff' }}
                data-testid="property-opp-delete-confirm"
              >
                Remover oportunidade
              </button>
            </div>
          </div>
        </div>
      ), document.body)}
    </div>
  );
};
